'use client';

/**
 * Edit chat — the sidebar panel that edits the widget on screen by talking to
 * a model. `useEditChat` owns the conversation (reducer in editChatState.js, the
 * POST + SSE stream + poll fallback); `EditChatPanel` only renders it.
 *
 * Fallback: when the backend has no edit-chat endpoint yet (GET history or
 * POST answers 404 without an error code), `available` turns false and the
 * page shows the old one-shot refine box instead.
 */

import {
  useCallback, useEffect, useMemo, useReducer, useRef, useState,
} from 'react';
import * as realApi from '../../services/ai-generator.service';
import * as mockApi from './editChatMock';
import { formatCredits } from '../../utils/aiPricing';
import {
  CHAT_MAX_LENGTH, QUESTIONS_FREE_NOTE, chargedLabel, chatReducer, chatSendError, initialChatState,
  isTurnSnapshotFinal, usualWaitLabel,
} from './editChatState';

const POLL_MS = 2500;
const TEXTAREA_MAX_PX = 160;

const formatClock = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

// A 404 from a missing route carries no `error` code; a 404 about a job
// (JOB_NOT_FOUND…) does — that one must not switch the chat off.
const isMissingEndpoint = (err) => err?.statusCode === 404 && !err.error && !err.code;

/**
 * @param {object} opts
 * @param {string|null} opts.token
 * @param {string|null} opts.jobId           job on screen (the base of the next turn)
 * @param {string[]} opts.knownJobIds        job ids of the current lineage
 * @param {boolean} opts.enabled             false → no requests (sample, logged out)
 * @param {(revision: object, userText: string) => void} opts.onRevision
 * @param {(credits: number) => void} opts.onCharged
 */
export function useEditChat({
  token, jobId, knownJobIds, enabled, onRevision, onCharged,
}) {
  const api = useMemo(() => (mockApi.isEditChatMockEnabled() ? mockApi : realApi), []);
  const [state, dispatch] = useReducer(chatReducer, initialChatState);
  const [available, setAvailable] = useState(null); // null = not known yet
  const [draft, setDraft] = useState('');
  const [elapsedMs, setElapsedMs] = useState(0);

  const streamRef = useRef(null);
  const pollRef = useRef(null);
  const turnRef = useRef(null); // { turnId, text, revisions:Set, doneApplied }
  const conversationJobsRef = useRef(new Set());
  const onRevisionRef = useRef(onRevision);
  const onChargedRef = useRef(onCharged);
  useEffect(() => {
    onRevisionRef.current = onRevision;
    onChargedRef.current = onCharged;
  });

  const stopTransport = useCallback(() => {
    streamRef.current?.close();
    streamRef.current = null;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  }, []);

  useEffect(() => stopTransport, [stopTransport]);

  // Side effects of a turn, once each, whichever transport delivered them.
  const applyRevision = useCallback((rev) => {
    const turn = turnRef.current;
    if (!turn || !rev?.job_id || turn.revisions.has(rev.job_id)) return;
    turn.revisions.add(rev.job_id);
    conversationJobsRef.current.add(rev.job_id);
    onRevisionRef.current?.(rev, turn.text);
  }, []);

  const applyDone = useCallback((done) => {
    const turn = turnRef.current;
    if (!turn || turn.doneApplied) return;
    turn.doneApplied = true;
    const charged = Number(done?.credits_charged) || 0;
    onChargedRef.current?.(charged);
  }, []);

  const handleEvent = useCallback((event, data) => {
    const payload = data || {};
    switch (event) {
    case 'step': dispatch({ type: 'step', text: payload.text }); break;
    case 'message': dispatch({ type: 'message', text: payload.text }); break;
    case 'revision':
      dispatch({ type: 'revision', ...payload });
      applyRevision(payload);
      break;
    case 'done':
      dispatch({ type: 'done', ...payload });
      applyDone(payload);
      stopTransport();
      break;
    case 'error':
      dispatch({ type: 'error', ...payload });
      stopTransport();
      break;
    default:
    }
  }, [applyRevision, applyDone, stopTransport]);

  // Poll fallback: the accumulated turn, until it is final.
  const startPolling = useCallback((turnId) => {
    if (pollRef.current) return;
    const tick = async () => {
      try {
        const snap = await api.getEditChatTurn(turnId, token);
        dispatch({ type: 'snapshot', snapshot: snap });
        if (snap?.revision) applyRevision(snap.revision);
        if (isTurnSnapshotFinal(snap)) {
          if (snap.status !== 'error' && snap.status !== 'failed' && !snap.error) {
            applyDone(snap.done || snap);
          }
          stopTransport();
        }
      } catch (err) {
        if (err?.statusCode === 404) {
          dispatch({ type: 'error', message: 'The edit was lost. Nothing was charged — please try again.' });
          stopTransport();
        }
        // Other errors: keep polling.
      }
    };
    pollRef.current = setInterval(tick, POLL_MS);
    tick();
  }, [api, token, applyRevision, applyDone, stopTransport]);

  // A new widget (not part of this conversation) → its own history.
  useEffect(() => {
    if (!enabled || !token || !jobId) return undefined;
    // Same conversation (a revision it produced, a restored version): keep it.
    if (conversationJobsRef.current.has(jobId)) return undefined;
    let alive = true;
    conversationJobsRef.current = new Set([jobId, ...(knownJobIds || [])]);
    dispatch({ type: 'reset' });
    api.getEditChatHistory(jobId, token)
      .then((data) => {
        if (!alive) return;
        setAvailable(true);
        (data?.messages || []).forEach((m) => {
          if (m?.job_id_after) conversationJobsRef.current.add(m.job_id_after);
        });
        dispatch({ type: 'history', rows: data?.messages || [], meta: data });
      })
      .catch((err) => {
        if (!alive) return;
        if (isMissingEndpoint(err)) setAvailable(false);
        else dispatch({ type: 'history', rows: [], meta: null });
      });
    return () => { alive = false; };
  // knownJobIds is read as of the switch; it must not re-trigger the load.
  }, [enabled, token, jobId, api]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lineage ids the page learns about later belong to this conversation too.
  useEffect(() => {
    (knownJobIds || []).forEach((id) => conversationJobsRef.current.add(id));
  }, [knownJobIds]);

  // Elapsed clock while a turn runs.
  const busy = state.status !== 'idle';
  const startedAt = state.turn?.startedAt;
  useEffect(() => {
    if (!busy || !startedAt) return undefined;
    const tick = () => setElapsedMs(Date.now() - startedAt);
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [busy, startedAt]);

  /**
   * Send `text` (default: the draft). Resolves to { fallback: true } when the
   * endpoint turned out not to exist (the caller moves the text to the refine
   * box).
   */
  const send = useCallback(async (raw) => {
    const text = String(raw ?? draft).trim().slice(0, CHAT_MAX_LENGTH);
    if (!text || busy || !jobId || !token) return { sent: false };
    conversationJobsRef.current.add(jobId);
    turnRef.current = {
      turnId: null, text, revisions: new Set(), doneApplied: false,
    };
    dispatch({
      type: 'send', text, jobId, now: Date.now(),
    });
    setDraft('');
    try {
      const data = await api.startEditChat(jobId, text, token);
      const turnId = data?.turn_id;
      turnRef.current.turnId = turnId;
      dispatch({
        type: 'started', turnId, estimate: data?.estimate_seconds || null, meta: data,
      });
      if (!turnId) {
        dispatch({ type: 'error', message: 'The edit could not be started. Nothing was charged.' });
        return { sent: false };
      }
      const stream = api.createEditChatSSE(turnId, token);
      streamRef.current = stream;
      stream.onEvent = handleEvent;
      stream.onBroken = () => {
        const t = turnRef.current;
        if (t?.turnId === turnId && !t.doneApplied) startPolling(turnId);
      };
      return { sent: true };
    } catch (err) {
      const info = chatSendError(err, state.allowance?.nextCost || 1);
      turnRef.current = null;
      dispatch({ type: 'sendFailed', error: info });
      setDraft(text);
      if (info.fallback && isMissingEndpoint(err)) {
        setAvailable(false);
        return { sent: false, fallback: true, text };
      }
      return { sent: false, error: info };
    }
  }, [draft, busy, jobId, token, api, handleEvent, startPolling, state.allowance]);

  return {
    state,
    available,
    draft,
    setDraft,
    send,
    busy,
    elapsedMs,
    clearError: () => dispatch({ type: 'clearError' }),
  };
}

function StepList({ steps, live }) {
  if (!steps || steps.length === 0) {
    return live ? (
      <ul className="ag-chat-steps">
        <li className="is-on">
          <span className="dot" aria-hidden="true" />
          Thinking…
        </li>
      </ul>
    ) : null;
  }
  return (
    <ul className="ag-chat-steps">
      {steps.map((step, i) => {
        const on = live && i === steps.length - 1;
        return (
          // Steps only ever append, so the index is a stable key.
          // eslint-disable-next-line react/no-array-index-key
          <li key={i} className={on ? 'is-on' : 'is-done'}>
            <span className="dot" aria-hidden="true" />
            {step}
          </li>
        );
      })}
    </ul>
  );
}

function ChatMessage({
  msg, currentJobId, onRestore, restoreDisabled,
}) {
  const pending = msg.status === 'pending';
  const tag = chargedLabel(msg);
  return (
    <li className={`ag-chat-msg ${msg.role}${msg.status === 'error' ? ' is-error' : ''}`}>
      <div className="ag-chat-bubble">
        {msg.text && <p className="ag-chat-text">{msg.text}</p>}
        {pending && <StepList steps={msg.steps} live />}
        {!pending && msg.steps.length > 0 && (
          <details className="ag-chat-trace">
            <summary>{`${msg.steps.length} ${msg.steps.length === 1 ? 'step' : 'steps'}`}</summary>
            <StepList steps={msg.steps} live={false} />
          </details>
        )}
        {msg.diff.length > 0 && (
          <ul className="ag-chat-diff">
            {msg.diff.map((d) => <li key={d}>{d}</li>)}
          </ul>
        )}
        {(msg.jobIdAfter || tag) && !pending && (
          <div className="ag-chat-meta">
            {tag && <span>{tag}</span>}
            {msg.jobIdAfter && (msg.jobIdAfter === currentJobId ? (
              <span className="ag-chat-current">Current version</span>
            ) : (
              <button
                type="button"
                className="ag-chat-restore"
                onClick={() => onRestore(msg.jobIdAfter)}
                disabled={restoreDisabled}
              >
                Restore this version
              </button>
            ))}
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * The chat panel. Purely presentational over useEditChat's result.
 */
export function EditChatPanel({
  chat, currentJobId, chips, priceLabel, priceCost, disabled, disabledReason,
  onRestore, restoreDisabled, onBuy,
}) {
  const {
    state, draft, setDraft, send, busy, elapsedMs,
  } = chat;
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const wasBusyRef = useRef(false);

  // Keep the newest message in view — inside the list, not the page.
  const last = state.messages[state.messages.length - 1];
  const lastKey = last ? `${last.id}:${last.steps.length}:${last.text.length}:${last.status}` : '';
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lastKey]);

  // Back to the input when a turn ends (it was disabled while running).
  useEffect(() => {
    if (wasBusyRef.current && !busy) inputRef.current?.focus({ preventScroll: true });
    wasBusyRef.current = busy;
  }, [busy]);

  // The input grows with its text up to a cap.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight + 2, TEXTAREA_MAX_PX)}px`;
  }, [draft]);

  const canSend = !disabled && !busy && draft.trim().length > 0;
  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (canSend) send();
    }
  };
  const onChip = (text) => {
    setDraft(text);
    inputRef.current?.focus();
  };

  const wait = usualWaitLabel(state.turn?.estimate);
  const empty = state.messages.length === 0;

  return (
    <section className="ag-chat" aria-label="Edit the widget in a chat">
      <div className="ag-chat-head">
        <span className="ag-chat-title">Edit with AI</span>
        <span className="ag-chat-price">{priceLabel}</span>
      </div>

      <ol
        className={`ag-chat-list${empty ? ' is-empty' : ''}`}
        ref={listRef}
        role="log"
        aria-live="polite"
        aria-relevant="additions text"
        aria-label="Conversation"
      >
        {empty && (
          <li className="ag-chat-hint">
            Ask for a change — “make the needle red”, “add an Alarm property” — or ask a question
            about the widget. Changes are made step by step and every version is kept.
          </li>
        )}
        {state.messages.map((msg) => (
          <ChatMessage
            key={msg.id}
            msg={msg}
            currentJobId={currentJobId}
            onRestore={onRestore}
            restoreDisabled={restoreDisabled || busy}
          />
        ))}
      </ol>

      {busy && (
        <div className="ag-chat-busy" role="status">
          <span className="spinner-border spinner-border-sm" aria-hidden="true" />
          <span className="ag-mono">{formatClock(elapsedMs)}</span>
          {wait && <span>{`· ${wait}`}</span>}
        </div>
      )}

      {state.error && (
        <div className="ag-chat-error" role="alert">
          <span>{state.error.message}</span>
          {state.error.buy && onBuy && (
            <button type="button" className="ag-btn ag-btn-ghost" onClick={onBuy}>Buy credits</button>
          )}
        </div>
      )}

      {!busy && chips.length > 0 && (
        <div className="ag-chat-chips" aria-label="Suggestions">
          {chips.map((c) => (
            <button key={c} type="button" className="ag-chip" onClick={() => onChip(c)} disabled={disabled}>
              {c}
            </button>
          ))}
        </div>
      )}

      <div className="ag-chat-compose">
        <textarea
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          rows={2}
          maxLength={CHAT_MAX_LENGTH}
          placeholder={disabledReason || 'Describe a change or ask a question'}
          disabled={disabled || busy}
          aria-label="Message to the editor"
          aria-describedby="ag-chat-note"
        />
        <button
          type="button"
          className="ag-btn ag-btn-primary"
          onClick={() => send()}
          disabled={!canSend}
        >
          {busy ? 'Working…' : 'Send'}
          {priceCost > 0 && <span className="cr">{formatCredits(priceCost)}</span>}
        </button>
      </div>
      <p className="ag-chat-note" id="ag-chat-note">
        {`Enter to send · Shift+Enter for a new line · ${QUESTIONS_FREE_NOTE}`}
      </p>
    </section>
  );
}
